/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * SERVICE : Calendar
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Ce service définit les types et utilitaires pour le calendrier.
 * La logique de récupération des données est gérée par :
 * - Data services : @/services/data/bookings et @/services/data/videoContent
 * - Store : @/stores/calendarStore pour la gestion d'état
 * 
 * TYPES D'ÉVÉNEMENTS :
 * 1. SHOOTING : Tournages confirmés avec Director
 * 2. PREPARATION : RDV de préparation avec Personal Brand Manager
 * 4. CONTENT : Publications vidéo programmées
 * 
 * LOGIQUE DE MAPPING :
 * - Les bookings contiennent `metadata.type` pour différencier shooting/preparation
 * - Les vidéos contiennent `metadata.publications` avec dates/channels
 * - Le service lie automatiquement les préparations à leur shooting associé
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { useCalendarStore } from '@/stores/calendarStore';

/**
 * Type d'événement dans le calendrier
 */
export type CalendarEventType = 'shooting' | 'editorial' | 'content' | 'ab_testing';

/**
 * Statut de production d'un contenu vidéo
 */
export type ContentStatus = 'in_production' | 'to_validate' | 'to_publish' | 'auto_validated' | 'published';

/**
 * Canaux de publication supportés
 */
export type Channel = 'linkedin' | 'instagram' | 'tiktok' | 'youtube' | 'facebook';

/**
 * Représente une publication programmée sur un canal spécifique
 */
export interface ChannelPublicationTime {
  channel: Channel;
  date: Date;
  time: string; // Format HH:MM
  pubId?: string; // ID from video_content_publications
  pubStatus?: string; // Status from video_content_publications (scheduled, draft, etc.)
}

/**
 * Représente un événement unifié dans le calendrier
 * Peut être un booking (shooting/preparation) ou un contenu vidéo
 */
export interface CalendarEvent {
  id: string;
  date: Date;
  type: CalendarEventType;
  title: string;
  description?: string;
  time?: string;
  endTime?: string;
  durationMinutes?: number;
  location?: string;
  locationAddress?: string; // Adresse complète pour le modal
  plan?: string;
  directorName?: string;
  directorAvatarUrl?: string;
  pbmName?: string;
  pbmAvatarUrl?: string;
  status?: 'upcoming' | 'pending' | 'completed';
  contentStatus?: ContentStatus;
  channels?: Channel[];
  publicationTimes?: ChannelPublicationTime[];
  currentDatePublications?: ChannelPublicationTime[];
  videoUrl?: string;
  coverUrl?: string;
  video2Url?: string;
  video2Thumbnail?: string;
  video3Url?: string;
  video3Thumbnail?: string;
  isMultihook?: boolean;
  viralityPredicted?: string | null;
  bookingUid?: string;
  bookingCalendlyUri?: string;
  relatedShootingDate?: Date; // For editorial events - linked shooting date
  linkedContentId?: string; // For AB testing cards - links to the content event
  isAbTesting?: boolean;
  calendarId?: string; // Unique per-card routing ID (e.g. `${video.id}_${dateKey}` or `${video.id}_ab`)
  linkedEditorialBooking?: {
    id: string;
    date: Date;
    time?: string;
    pbmName?: string;
  }; // For shooting events - linked editorial booking
  shootingId?: string; // For shooting events - the actual shooting ID (different from booking ID)
  isDraft?: boolean; // True when video is saved as draft (client_review with no validation intent)
  instagramCrosspostDisabled?: boolean; // Per-content override of global Instagram crosspost setting
  versionAEnabled?: boolean;
  versionBEnabled?: boolean;
  versionCEnabled?: boolean;
}

/**
 * Mappe le statut brut de video_content vers ContentStatus standardisé
 * 
 * Statuts DB:
 * - draft: Vidéo en cours de création
 * - processing: Vidéo en cours de traitement/validation
 * - ready: Vidéo validée et prête à être publiée
 * - published: Vidéo publiée
 * 
 * @param status - Statut brut depuis la table video_content
 * @returns ContentStatus normalisé pour l'UI
 */
export const mapVideoStatus = (status: string): ContentStatus => {
  switch (status) {
    case 'draft':
    case 'shot':
    case 'derush_pending':
    case 'derush_done':
    case 'editing_pending':
    case 'editing_review':
      return 'in_production';
    case 'redelivery_review':
      return 'in_production';
    case 'client_review':
    case 'client_feedback':
    case 'processing':
      return 'to_validate';
    case 'scheduled':
      return 'to_publish';
    case 'to_publish':
      return 'auto_validated';
    case 'published':
      return 'published';
    default:
      return 'in_production';
  }
};

/**
 * Extrait les publications programmées depuis les metadata d'une vidéo
 * 
 * Les metadata contiennent un array `publications` avec :
 * - channel : Nom du canal (linkedin, instagram, etc.)
 * - date : Date de publication au format ISO
 * - time : Heure de publication (HH:MM)
 * 
 * @param metadata - Objet metadata depuis video_content
 * @returns Array de publications parsées et normalisées
 */
export const extractPublications = (metadata: any): ChannelPublicationTime[] => {
  if (!metadata || !metadata.publications) return [];
  
  return metadata.publications.map((pub: any) => ({
    channel: pub.channel as Channel,
    date: new Date(pub.date),
    time: pub.time
  }));
};

/**
 * @deprecated Use useCalendarStore from @/stores/calendarStore instead
 * Cette fonction est conservée pour la compatibilité mais délègue au store
 * 
 * @param userId - UUID de l'utilisateur
 * @returns { success, data, error? } avec array d'événements unifiés
 */
export const getCalendarEvents = async (userId: string) => {
  const store = useCalendarStore.getState();
  await store.fetchCalendarEvents(userId);
  
  // Get updated state after fetch completes
  const updatedState = useCalendarStore.getState();
  
  return {
    success: updatedState.error === null,
    data: updatedState.events,
    error: updatedState.error
  };
};

/**
 * @deprecated Use useCalendarStore.updateVideoPublications instead
 * Cette fonction est conservée pour la compatibilité mais délègue au store
 * 
 * @param videoId - UUID de la vidéo à mettre à jour
 * @param publications - Array de publications avec channel, date, time
 * @returns { success, error? }
 */
export const updateVideoPublications = async (
  videoId: string,
  publications: ChannelPublicationTime[],
  userId?: string
) => {
  const store = useCalendarStore.getState();
  const success = await store.updateVideoPublications(videoId, publications, userId);

  return { success, error: success ? undefined : new Error('Failed to update publications') };
};
